import type { ParkReason } from '@shared/types';
import { cn } from '@web/lib/cn';

/**
 * Why the assistant handed this over. The queue is a pull queue, so this is the
 * field someone reads to decide which conversation to take next — it has to be
 * legible in one glance, which is why the compact form is a four-to-six
 * character mono token rather than a sentence squeezed into a column.
 */

const FULL: Record<ParkReason, string> = {
  asked_for_human: 'Asked for a human',
  frustrated: 'Frustrated',
  ai_stuck: 'Assistant stuck',
  low_confidence: 'Low confidence',
  money_question: 'Money question',
  missing_data: 'Missing data',
  complaint: 'Complaint',
  ai_error: 'Assistant error',
  out_of_hours: 'Out of hours',
  manual: 'Taken manually',
};

const SHORT: Record<ParkReason, string> = {
  asked_for_human: 'ASKED',
  frustrated: 'FRUST',
  ai_stuck: 'STUCK',
  low_confidence: 'CONF',
  money_question: 'MONEY',
  missing_data: 'DATA',
  complaint: 'CMPLNT',
  ai_error: 'ERROR',
  out_of_hours: 'HOURS',
  manual: 'MANUAL',
};

/**
 * Two reasons are a person's temper rather than a gap in the assistant's
 * knowledge, and they are the ones that go cold fastest — so they, and only
 * they, are drawn in the reserved hue.
 */
const URGENT = new Set<ParkReason>(['frustrated', 'complaint']);

export function ReasonChip({
  reason,
  size = 'compact',
  className,
}: {
  reason: ParkReason | null;
  size?: 'compact' | 'full';
  className?: string;
}) {
  if (reason === null) return null;
  const urgent = URGENT.has(reason);

  return (
    <span
      className={cn(
        'inline-flex h-5 items-center whitespace-nowrap',
        size === 'compact' ? 'px-1.5 font-mono text-[10px] tracking-[0.08em]' : 'px-2 text-[11px]',
        'border-l-2',
        urgent ? 'border-signal bg-signal/10 text-signal' : 'border-rule-strong text-ink-dim',
        className,
      )}
      title={FULL[reason]}
    >
      {size === 'compact' ? SHORT[reason] : FULL[reason]}
      {size === 'compact' && <span className="sr-only"> — {FULL[reason]}</span>}
    </span>
  );
}
